/*
 * Live Search page — re-styled to match the Calendar / Home / Day-of-
 * school design language: cream card on the theme bg, engine picker
 * as orange-bordered pills, clean labeled inputs, single accent
 * Search button per row.
 *
 * Sized to content like the Weather panel — pageTransition's flex
 * column with justify-content: center handles vertical centering, so
 * the card never grows into a vast empty rectangle.
 *
 * The redundant "← Home" / "← Classrooms" header buttons that lived
 * here in the original port are gone — the docked LiveBackButton (top-
 * left) and bottom MmPageNav already cover those flows.
 */

.search {
  position: relative;
  z-index: 1;
  /* Edge + chrome clearance is handled by the live stage safe-area. */
  padding: 0;
  font-family: var(--token-font-ui);
  color: var(--token-text-primary);
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
}

.empty {
  position: relative;
  z-index: 1;
  padding: var(--token-space-2xl);
  font-family: var(--token-font-ui);
}

.card {
  /* Same frosted-white fill as the other live cards (Rewards, Weather,
   * Pledges) — at the old 0.45 the theme art read straight through and
   * the labels/inputs lost contrast. */
  background: rgba(255, 250, 240, 0.86);
  backdrop-filter: blur(22px) saturate(160%);
  -webkit-backdrop-filter: blur(22px) saturate(160%);
  border: 1.5px solid var(--token-accent);
  border-radius: 24px;
  padding: 26px clamp(28px, 3vw, 48px) 22px;
  box-shadow:
    0 30px 60px -20px rgba(8, 18, 12, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 920px;
  margin: 0 auto;
  width: 100%;
}

/* ---------- Header row (title + engine picker on one line) ---------- */

.head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px 22px;
}

.titleWrap {
  display: flex;
  align-items: center;
  gap: 10px;
}

.titleGlyph {
  font-size: clamp(26px, 2.4vw, 36px);
  line-height: 1;
  filter: drop-shadow(0 2px 4px color-mix(in srgb, var(--token-accent) 35%, transparent));
}

.title {
  margin: 0;
  font-family: var(--token-font-display);
  font-size: clamp(28px, 2.6vw, 42px);
  line-height: 1;
  color: var(--token-accent);
  letter-spacing: 0.5px;
}

/* ---------- Engine picker — pushed to the right of the title ---------- */

.engineRow {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  border: 0;
  padding: 0;
  margin: 0 0 0 auto;
  align-items: center;
}

.engineButton {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: var(--token-font-ui);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.2px;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1.5px solid rgba(28, 38, 32, 0.18);
  background: rgba(255, 255, 255, 0.6);
  color: var(--token-text-primary);
  cursor: pointer;
  transition:
    background 120ms ease,
    color 120ms ease,
    border-color 120ms ease;
}

.engineButton:hover:not([aria-checked='true']) {
  background: #fff;
  border-color: rgba(28, 38, 32, 0.32);
}

.engineButton[aria-checked='true'] {
  background: var(--token-accent);
  color: #fff;
  border-color: var(--token-accent);
}

/* Per-engine accent dot rendered before the label. Helps each engine
 * read at a glance without making them feel like buttons. */
.engineDot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  flex-shrink: 0;
}

.engineDotKiddle {
  background: #4caf50;
}

.engineDotGoogle {
  background: linear-gradient(45deg, #4285f4 0%, #ea4335 50%, #fbbc04 100%);
}

.engineDotBing {
  background: #008373;
}

.engineButton[aria-checked='true'] .engineDot {
  background: rgba(255, 255, 255, 0.95);
}

/* ---------- Search form rows ---------- */

.divider {
  height: 1px;
  background: color-mix(in srgb, var(--token-accent) 22%, transparent);
  border: 0;
  margin: 4px 0 0;
}

.form {
  display: flex;
  align-items: stretch;
  gap: 10px;
}

.field {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
}

.fieldLabel {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: var(--token-font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: rgba(28, 38, 32, 0.6);
}

.fieldGlyph {
  font-size: 14px;
  line-height: 1;
}

.input {
  font-family: var(--token-font-body);
  font-size: 16px;
  color: var(--token-text-primary);
  padding: 11px 16px;
  border-radius: 12px;
  border: 1px solid rgba(28, 38, 32, 0.16);
  background: rgba(255, 255, 255, 0.85);
  width: 100%;
  transition:
    border-color 120ms ease,
    box-shadow 120ms ease,
    background 120ms ease;
  box-sizing: border-box;
}

.input::placeholder {
  color: rgba(28, 38, 32, 0.4);
}

.input:focus {
  outline: none;
  border-color: var(--token-accent);
  background: #fff;
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--token-accent) 16%, transparent);
}

.submit {
  align-self: flex-end;
  font-family: var(--token-font-ui);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.4px;
  padding: 11px 22px;
  border-radius: 12px;
  border: 1.5px solid var(--token-accent);
  background: var(--token-accent);
  color: #fff;
  cursor: pointer;
  transition:
    background 120ms ease,
    border-color 120ms ease;
  white-space: nowrap;
}

.submit:hover {
  background: color-mix(in srgb, var(--token-accent) 85%, #000);
  border-color: color-mix(in srgb, var(--token-accent) 85%, #000);
}
